import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  setCompany,
  setCountryData,
  setSearch,
  setSearchCount,
  setSearchResults,
} from "../../store/search/searchSlice";
import { setCompanyData } from "../../store/Company/companySlice";
import { setFilledCompany } from "../../store/claim/claimSlice";
import { useIndividualCompanyMutation } from "../../store/Company/companyApiSlice";
import useSearchCompany from "../useSearchCompany";
import useCountryCode from "../useCountryCode";
import { Countries } from "../../components/public/Home/CountryFlag";

/**
 * "Search your Business" — the step that finds the company before the claim
 * intake collects anything about it.
 *
 * Transcribed from components/public/SignUp/CompanyProfile/SearchCompany.jsx,
 * which is what /sign-up shows at redirectToStep 6 today:
 *  - the same useSearchCompany call, with the same three-character gate and
 *    400ms debounce before a query is issued;
 *  - the same publish rule for results (TIP-599: only clear the list once a
 *    search has actually completed and genuinely came back empty, because the
 *    hook starts at [] and an empty array is truthy);
 *  - the same validation order — country, then a non-empty name, then an
 *    actual selection;
 *  - the same two save branches: an existing TREVEX company is linked to the
 *    individual via individualCompany, anything else is written to the claim
 *    slice as filledCompany with new_company 0.
 *
 * The "cannot find the business" path is transcribed from CompanyNotFound.jsx,
 * the sibling this screen switches to today: the user types the legal name
 * themselves and it is written as filledCompany with new_company 1 and an
 * empty wb_win. It stays on this screen rather than navigating away — the
 * original toggles between the two modes in place.
 *
 * The original is untouched; /sign-up still renders it.
 */

/* Identity for "is this the row I picked?" — a company may arrive with a
   TREVEX id, a WorldBox win, or neither. */
/* Three characters before a query is worth issuing — the original's gate. */
export const MIN_QUERY = 3;

export const companyKey = (c) =>
  c ? c.companyID || c.wb_win || c.win || c.name || null : null;

export default function useListBusiness({ onSelected = () => {}, enabled = true } = {}) {
  const dispatch = useDispatch();
  const { token, userInfo } = useSelector((state) => state.auth);
  const { searchQuery, searchResults, worldBoxToken, countryData, companySelectedByUser } =
    useSelector((state) => state.search);

  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [individualCompany] = useIndividualCompanyMutation();
  /* "Locate Using GPS" resolves to the code useCountryCode has already derived,
     exactly as the production bar's handleLocationPopup does. */
  const { countryCode: detectedCode } = useCountryCode();

  const countryCode = countryData?.code || "AE";

  /* `enabled` is false when the company came from the URL: there is nothing to
     search for, and issuing the query would be a wasted request. */
  const { data, isLoading, message } = useSearchCompany(
    enabled ? searchQuery : "",
    countryCode,
    token,
    worldBoxToken,
    enabled
  );

  /* TIP-599, transcribed: the hook starts out with data = [], and an empty
     array is truthy, so a plain `if (data)` would wipe a result set carried in
     from /search before this screen's own search had run. */
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (isLoading) hasFetchedRef.current = true;
  }, [isLoading]);

  useEffect(() => {
    if (data?.length) {
      dispatch(setSearchResults(data));
      dispatch(setSearchCount(data.length));
    } else if (hasFetchedRef.current && !isLoading) {
      dispatch(setSearchResults([]));
      dispatch(setSearchCount(0));
    }
  }, [data, isLoading, dispatch]);

  /* Debounced so typing a name does not issue a request per keystroke. */
  const debouncedSearch = useCallback(() => {
    let timeout;
    return (value) => {
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        dispatch(setSearch(value));
      }, 400);
    };
  }, [dispatch])();

  const changeName = (value) => {
    setName(value);
    setErrors((e) => ({ ...e, empty: undefined, notSelected: undefined }));

    /* Under three characters there is nothing worth querying, and the previous
       results no longer describe what is typed. */
    if (value.trim().length < MIN_QUERY) {
      dispatch(setSearchResults([]));
      dispatch(setSearchCount(0));
      dispatch(setCompany(null));
      return;
    }
    debouncedSearch(value);
  };

  const changeCountry = (code) => {
    const detail = { code };
    dispatch(setCountryData(detail));
    setErrors((e) => ({ ...e, country: undefined }));
  };

  const locateCountry = (choose) => {
    const match = Countries.find((c) => c.code === detectedCode);
    if (match) choose(match);
  };

  const selectCompany = (company) => {
    dispatch(setCompany(company));
    setErrors((e) => ({ ...e, notSelected: undefined }));
  };

  /* Validation order is the original's: country, then a name, then a pick. */
  const save = async () => {
    const next = {};

    if (!countryCode) {
      setErrors({ country: "Please select country." });
      return;
    }
    if (name.trim() === "") {
      setErrors({ empty: "Please enter business name." });
      return;
    }
    if (name.trim().length < MIN_QUERY) {
      setErrors({ empty: `Enter at least ${MIN_QUERY} characters to search.` });
      return;
    }
    if (!companySelectedByUser?.name && !companySelectedByUser?.companyID) {
      setErrors({ notSelected: "Please Select a business" });
      return;
    }

    setErrors({});
    setSaving(true);
    try {
      if (companySelectedByUser?.companyID) {
        const linked = await individualCompany({
          data: {
            individualID: userInfo?.individual?.[0]?.individualID,
            companyID: companySelectedByUser.companyID,
          },
          token,
        });
        if (linked) {
          dispatch(setCompanyData(companySelectedByUser));
          onSelected(companySelectedByUser);
        }
      } else {
        dispatch(
          setFilledCompany({
            registered_company_name: companySelectedByUser.name,
            wb_win: companySelectedByUser?.win,
            country_code: countryCode,
            new_company: 0,
            companyID: companySelectedByUser?.companyID,
          })
        );
        onSelected(companySelectedByUser);
      }
    } catch (error) {
      next.backend = error?.data || "Internal Server Error. Please try again later.";
      setErrors(next);
    } finally {
      setSaving(false);
    }
  };

  /* "Cannot find the business" — the user types the legal name themselves.
     Transcribed from CompanyNotFound: new_company 1 and no wb_win, which is
     what tells the claim intake there is no directory record behind this. */
  const saveNewBusiness = () => {
    if (name.trim() === "") {
      setErrors({ empty: "Please enter business name." });
      return;
    }
    setErrors({});
    dispatch(
      setFilledCompany({
        registered_company_name: name.trim(),
        wb_win: "",
        country_code: countryCode,
        new_company: 1,
      })
    );
    onSelected(null);
  };

  /* Switching modes drops any pick made in the other one, as the original's
     handleSwitch does — a chosen directory row is meaningless once the user
     has said their business is not in the directory. */
  const switchMode = (toNotFound) => {
    dispatch(setCompany(null));
    setErrors({});
    setNotFound(toNotFound);
  };

  /* The search slice is app-wide, so results from /search are already in it when this screen mounts. */
  const searching = name.trim().length >= MIN_QUERY;

  return {
    name,
    changeName,
    searching,
    minQuery: MIN_QUERY,
    saveNewBusiness,
    switchMode,
    countryCode,
    changeCountry,
    locateCountry,
    results: searching ? searchResults || [] : [],
    isLoading,
    message,
    selected: companySelectedByUser,
    selectedKey: companyKey(companySelectedByUser),
    selectCompany,
    save,
    saving,
    errors,
    notFound,
    setNotFound,
  };
}
